import { useState } from "react";

import { default as styled } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { Chip } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/chip/chip";
import { TextField } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/textfield/textfield";

const KeywordContainer = styled(Flex)`
    flex-wrap: wrap;
    max-height: 170px;
    overflow: auto;
    border: 1px solid ${colorsPalettes.midnight["50"]};
    border-bottom: none;

    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    & + .keyword-input {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }
`;

interface IKeywordManagerProps {
    title: string;
    keywords?: string[];
    placeholder?: string;
    strikethrough?: boolean;
    addButtonText?: string;
    onKeywordsChange: (keywords: string[]) => void;
}

export const KeywordManager = ({
    title,
    keywords = [],
    onKeywordsChange,
    placeholder,
    strikethrough,
    addButtonText = "Add",
}: IKeywordManagerProps) => {
    const [inputValue, setInputValue] = useState("");

    const parseKeywords = (input: string): string[] => {
        return input
            .split(",")
            .map((keyword) => keyword.trim())
            .filter((keyword) => keyword.length > 0);
    };

    const getUniqueKeywords = (input: string): string[] => {
        const parsedKeywords = parseKeywords(input);
        const keywordSet = new Set(keywords);
        const newKeywordsSet = new Set(parsedKeywords);

        return [...newKeywordsSet.difference(keywordSet)];
    };

    const handleAddKeyword = () => {
        const uniqueKeywords = getUniqueKeywords(inputValue);
        if (uniqueKeywords.length > 0) {
            const keywordSet = new Set(keywords);
            const newKeywordsSet = new Set(uniqueKeywords);
            const combined = keywordSet.union(newKeywordsSet);
            onKeywordsChange([...combined]);
            setInputValue("");
        }
    };

    const handleRemoveKeyword = (keywordToRemove: string) => {
        onKeywordsChange(keywords.filter((keyword) => keyword !== keywordToRemove));
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === " ") {
            event.stopPropagation();
        }
    };

    const handleSubmit = (value: string) => {
        const uniqueKeywords = getUniqueKeywords(value);
        if (uniqueKeywords.length > 0) {
            onKeywordsChange([...keywords, ...uniqueKeywords]);
            setInputValue("");
        }
    };

    const isAddButtonDisabled = getUniqueKeywords(inputValue).length === 0;

    return (
        <Stack
            width="500px"
            gap="16px"
        >
            <Flex>
                <Text
                    fontVariant="body1"
                    color={colorsPalettes.carbon[400]}
                >
                    {title}
                </Text>
            </Flex>
            <Stack>
                {keywords.length > 0 ? (
                    <KeywordContainer
                        padding="8px 12px"
                        borderRadius="3px"
                        gap="8px"
                    >
                        {keywords.map((keyword, index) => (
                            <Chip
                                key={index}
                                strikethrough={strikethrough}
                                onCloseItem={() => handleRemoveKeyword(keyword)}
                                text={keyword}
                            />
                        ))}
                    </KeywordContainer>
                ) : null}
                <TextField
                    className="keyword-input"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                    placeholder={placeholder}
                    leftAddon={
                        <SWReactIcons
                            iconName="keywords"
                            size="sm"
                            iconColor={colorsPalettes.carbon["200"]}
                        />
                    }
                    rightAddon={
                        inputValue.trim() ? (
                            <Button
                                type="flat"
                                isDisabled={isAddButtonDisabled}
                                onClick={handleAddKeyword}
                            >
                                {addButtonText}
                            </Button>
                        ) : null
                    }
                />
            </Stack>
        </Stack>
    );
};
